import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ChromePicker } from 'react-color';
import * as Yup from 'yup';
import { parsePhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api, { getCsrfToken, createEnterpriseClient } from '../../../services/api';
import ThemeContext from '../../../ThemeContext';
import ResellerConfig from '../../../../config/ResellerConfig';
import { URL_SEGMENTS } from '../../../../config/Routesconfig';
// Validation schemas
const nameSchema = Yup.string()
  .required('Please fill this field.')
  .min(3, 'Name must contain at least 3 letters.')
  .matches(/^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/, 'Name should not start or end with a number or special character.');
const emailSchema = Yup.string()
  .required('Please fill this field.')
  .min(8, 'Email must be at least 8 characters long.')
  .matches(/[^.!@}]/, 'Email must not start with a dot (.), exclamation mark (!), at symbol (@), or curly brace (}).')
  .matches(/^[^@]*@[^@]*$/, 'Email must contain exactly one @ symbol.')
  .matches(/@[^.]*\.[^.].*/, 'Email must include a domain after @ (e.g., example.com).')
  .matches(/@.*\.[a-zA-Z]{2,}$/, 'Email must end with a valid domain extension (e.g., .com, .org) containing only letters.')
  .matches(/@.*\.[^.]*/, 'Email must include a domain after . (e.g., example.com).')
  .matches(/@.*[a-zA-Z]{2,}\./, 'Domain name must have at least 2 letters before the dot.')
  .matches(/@.*\.[a-zA-Z]{2,}$/, 'Domain extension must have at least 2 letters after the dot.')
  .matches(/^[^.].*[^.]$/, 'Email cannot start or end with a dot.')
  .matches(/^(?!.*\.\.).*$/, 'Email cannot contain consecutive dots (e.g., ..).')
  .email('Invalid email address.');
const phoneSchema = Yup.string()
  .required('Mobile is required.')
  .test('valid-mobile', 'Invalid mobile number.', function (value) {
    if (!value) return false;
    try {
      const phoneToValidate = value.startsWith('+') ? value : `+${value}`;
      const phoneNumber = parsePhoneNumber(phoneToValidate);
      return phoneNumber && phoneNumber.isValid();
    } catch {
      return false;
    }
  });
const zipSchema = Yup.string()
  .required('Please fill this field.')
  .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits.');
const passwordSchema = Yup.string()
  .required('Please fill this field.')
  .min(8, 'Password must be at least 8 characters.')
  .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
  .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
  .matches(/[0-9]/, 'Password must contain at least one number.')
  .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character.');
const basicSchema = Yup.object({
  client_name: nameSchema,
  start_date: Yup.date().nullable().required('Start Date is required.'),
  primary_name: nameSchema,
  primary_email: emailSchema,
  primary_phone: phoneSchema,
  country: Yup.string().required('Country is required.'),
  address: Yup.string().required('Address is required.'),
  city: Yup.string().required('City is required.'),
  state: Yup.string().required('State is required.'),
  zip: zipSchema,
});
const adminSchema = Yup.object({
  first_name: nameSchema,
  last_name: nameSchema,
  email: emailSchema,
  username: Yup.string().required('Please fill this field.'),
  password: passwordSchema,
  password_confirmation: Yup.string()
    .required('Please fill this field.')
    .oneOf([Yup.ref('password'), null], 'Passwords do not match.'),
});

const AddEnterpriseClient = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { username } = useParams();
  const navigate = useNavigate();
  const today = new Date();

  // ── Config ───
  const cfg = ResellerConfig.enterpriseClient; // label, emptyMessage

  const [formData, setFormData] = useState({
    client_name: '',
    start_date: today.toISOString().split('T')[0],
    location: '',
    primary_name: '',
    primary_email: '',
    primary_phone: '',
    country: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    enterprise_client_description: '',
    text_color: '#000000',
    banner_color: '#ffffff',
    enterprise_client_logo: null,
    url: '',
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    password_confirmation: '',
    reseller_id: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    client_name: false,
    start_date: false,
    location: false,
    primary_name: false,
    primary_email: false,
    primary_phone: false,
    country: false,
    address: false,
    city: false,
    state: false,
    zip: false,
    enterprise_client_description: false,
    text_color: false,
    banner_color: false,
    enterprise_client_logo: false,
    url: false,
    first_name: false,
    last_name: false,
    email: false,
    username: false,
    password: false,
    password_confirmation: false,
  });
  const [activeTab, setActiveTab] = useState('basic');
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('user')) || {});
  const [isReseller, setIsReseller] = useState(currentUser.role === 'reseller');
  const [isLoading, setIsLoading] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBannerColorPicker, setShowBannerColorPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [checking, setChecking] = useState({ email: false, mobile: false });
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState(today);
  const textColorPickerRef = useRef(null);
  const bannerColorPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const iconRef = useRef(null);
  const calendarRef = useRef(null);
  const [logoName, setLogoName] = useState('No file chosen');
  let hideTimer = null;

  const clearHideTimer = () => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };
  const startHideTimer = () => {
    clearHideTimer();
    hideTimer = setTimeout(() => {
      setShowDatePicker(false);
    }, 150);
  };
  const handleIconMouseEnter = () => {
    clearHideTimer();
    setShowDatePicker(true);
  };
  const handleIconMouseLeave = () => {
    startHideTimer();
  };
  const handleCalendarMouseEnter = () => {
    clearHideTimer();
    setShowDatePicker(true);
  };
  const handleCalendarMouseLeave = () => {
    startHideTimer();
  };
  const handleDateIconClick = () => {
    clearHideTimer();
    setShowDatePicker((prev) => !prev);
  };
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData((prev) => {
      const updated = { ...prev, start_date: date.toISOString().split('T')[0] };
      try {
        basicSchema.validateSyncAt('start_date', updated);
        setErrors((prevE) => ({ ...prevE, start_date: '' }));
      } catch (err) {
        setErrors((prevE) => ({ ...prevE, start_date: err.message }));
      }
      return updated;
    });
    setTouched((prev) => ({ ...prev, start_date: true }));
    setShowDatePicker(false);
  };

  useEffect(() => {
    console.log('AddEnterpriseClient mounted, username:', username, 'role:', currentUser.role);
    if (currentUser.role !== 'reseller') {
      toast.error('Unauthorized access', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
        autoClose: 3000,
      });
      navigate(`/${currentUser.username}/dashboard`, { replace: true });
    }
  }, [currentUser.role, navigate, theme.colors, username]);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const fetchCountries = debounce(async () => {
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries');
      const data = await response.json();
      if (data.error) {
        throw new Error('Failed to fetch countries');
      }
      setCountries(data.data.map(country => country.country));
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries(['United States', 'Canada', 'Other']);
    }
  }, 500);

  const fetchStates = async (country) => {
    if (!country) {
      setStatesList([]);
      return;
    }
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country })
      });
      const data = await response.json();
      if (data.error || !data.data) {
        throw new Error(data.msg || 'Failed to fetch states');
      }
      const states = data.data.states || [];
      setStatesList(states.map(state => state.name));
    } catch (error) {
      console.error('Error fetching states:', error);
      setStatesList([]);
    }
  };

  useEffect(() => {
    fetchCountries();
    const fetchCsrf = async () => {
      try {
        await getCsrfToken();
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
        toast.error('Failed to fetch CSRF token.', {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
          },
          autoClose: 3000,
        });
      }
    };
    fetchCsrf();
    setFormData((prev) => ({
      ...prev,
      reseller_id: currentUser.id,
    }));
  }, [currentUser.id, theme.colors]);

  useEffect(() => {
    if (formData.country) {
      fetchStates(formData.country);
      setFormData((prev) => ({ ...prev, state: '' }));
    } else {
      setStatesList([]);
      setFormData((prev) => ({ ...prev, state: '' }));
    }
  }, [formData.country]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };
      if (name === 'primary_email') {
        updatedFormData.email = value;
      }
      if (name === 'email') {
        updatedFormData.primary_email = value;
      }
      return updatedFormData;
    });
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (['client_name', 'primary_name', 'first_name', 'last_name'].includes(name) && value.length > 0) {
      if (/^[0-9!@#$%^&*(),.?":{}|<>]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name === 'client_name' ? 'Client Name' : name === 'primary_name' ? 'Primary Contact Name' : name === 'first_name' ? 'First Name' : 'Last Name'} must not start with a number or special character.`,
        }));
        return;
      }
      if (value.length < 3) {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name === 'client_name' ? 'Client Name' : name === 'primary_name' ? 'Primary Contact Name' : name === 'first_name' ? 'First Name' : 'Last Name'} must be at least 3 characters.`,
        }));
        return;
      }
    }
    if (['primary_email', 'email'].includes(name) && value.length > 0) {
      if (value.length >= 8 && !value.includes('@')) {
        setErrors((prev) => ({
          ...prev,
          [name]: 'Email must contain exactly one @ symbol.',
        }));
        return;
      }
      if (value.includes('@')) {
        const parts = value.split('@');
        if (parts[1].length === 0) {
          setErrors((prev) => ({
            ...prev,
            [name]: 'Email must include a domain after @ (e.g., example.com).',
          }));
          return;
        }
        if (parts[1].includes('.') && parts[1].split('.')[0].length < 2) {
          setErrors((prev) => ({
            ...prev,
            [name]: 'Domain name must have at least 2 letters before the dot.',
          }));
          return;
        }
        if (!parts[1].includes('.')) {
          setErrors((prev) => ({
            ...prev,
            [name]: 'Email must contain exactly one (.) dot (e.g., example.com).',
          }));
          return;
        }
        if (parts[1].includes('.') && parts[1].split('.')[1].length < 2) {
          setErrors((prev) => ({
            ...prev,
            [name]: 'Domain extension must have at least 2 letters after the dot.',
          }));
          return;
        }
      }
    }
    if (name === 'zip' && value.length > 0) {
      if (!/^\d{5}$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          zip: 'Zip code must be exactly 5 digits.',
        }));
        return;
      }
    }
    if (name === 'password' && value.length > 0) {
      if (value.length < 8) {
        setErrors((prev) => ({
          ...prev,
          password: 'Password must be at least 8 characters.',
        }));
        return;
      }
      if (!/[A-Z]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          password: 'Password must contain at least one uppercase letter.',
        }));
        return;
      }
      if (!/[a-z]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          password: 'Password must contain at least one lowercase letter.',
        }));
        return;
      }
      if (!/[0-9]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          password: 'Password must contain at least one number.',
        }));
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          password: 'Password must contain at least one special character.',
        }));
        return;
      }
    }
    if (name === 'password_confirmation' && value.length > 0) {
      if (value !== formData.password) {
        setErrors((prev) => ({
          ...prev,
          password_confirmation: 'Passwords do not match.',
        }));
        return;
      }
    }
    try {
      let schemaToUse = basicSchema;
      if (['first_name', 'last_name', 'email', 'username', 'password', 'password_confirmation'].includes(name)) {
        schemaToUse = adminSchema;
      }
      await schemaToUse.validateAt(name, { ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: '' }));
      if (name === 'primary_email' || name === 'email') {
        setChecking((prev) => ({ ...prev, email: true }));
        await api.post('/check-email', { email: value });
        setChecking((prev) => ({ ...prev, email: false }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, enterprise_client_logo: 'The logo must be an image (JPEG, PNG, GIF, BMP, or WebP).' }));
        setFormData((prev) => ({ ...prev, enterprise_client_logo: null }));
        setLogoName('No file chosen');
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        return;
      }
      if (file.size > 2048 * 1024) {
        setErrors((prev) => ({ ...prev, enterprise_client_logo: 'The logo must not exceed 2MB.' }));
        setFormData((prev) => ({ ...prev, enterprise_client_logo: null }));
        setLogoName('No file chosen');
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        return;
      }
      setFormData((prev) => ({ ...prev, enterprise_client_logo: file }));
      setLogoName(file.name);
      setErrors((prev) => ({ ...prev, enterprise_client_logo: '' }));
    } else {
      setFormData((prev) => ({ ...prev, enterprise_client_logo: null }));
      setLogoName('No file chosen');
      setErrors((prev) => ({ ...prev, enterprise_client_logo: '' }));
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (!value && ['client_name', 'start_date', 'primary_name', 'primary_email', 'country', 'address', 'city', 'state', 'zip', 'first_name', 'last_name', 'email', 'username', 'password', 'password_confirmation'].includes(name)) {
      setErrors((prev) => ({ ...prev, [name]: 'Please fill this field.' }));
      return;
    }
    try {
      let schemaToUse = basicSchema;
      if (['first_name', 'last_name', 'email', 'username', 'password', 'password_confirmation'].includes(name)) {
        schemaToUse = adminSchema;
      }
      await schemaToUse.validateAt(name, { ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: '' }));
      if (name === 'primary_email' || name === 'email') {
        setChecking((prev) => ({ ...prev, email: true }));
        await api.post('/check-email', { email: value });
        setChecking((prev) => ({ ...prev, email: false }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const toggleTextColorPicker = () => {
    setShowTextColorPicker((prev) => !prev);
    if (showBannerColorPicker) {
      setShowBannerColorPicker(false);
    }
  };
  const toggleBannerColorPicker = () => {
    setShowBannerColorPicker((prev) => !prev);
    if (showTextColorPicker) {
      setShowTextColorPicker(false);
    }
  };

  const validateTab = async (tab) => {
    try {
      const schema = tab === 'basic' ? basicSchema : adminSchema;
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationErrors) {
      const newErrors = {};
      validationErrors.inner.forEach((err) => {
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateTab('admin'))) {
      toast.error('Please fill all required fields correctly.', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
        autoClose: 3000,
      });
      return;
    }
    setIsLoading(true);
    const payload = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === 'enterprise_client_logo' && formData[key] instanceof File) {
        payload.append('enterprise_client_logo', formData[key]);
      } else {
        payload.append(key, formData[key]);
      }
    });
    try {
      const response = await createEnterpriseClient(payload);
      setIsLoading(false);
      toast.success(response.data.message, {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
        autoClose: 3000,
      });
      setFormData({
        client_name: '',
        start_date: today.toISOString().split('T')[0],
        location: '',
        primary_name: '',
        primary_email: '',
        primary_phone: '',
        country: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        enterprise_client_description: '',
        text_color: '#000000',
        banner_color: '#ffffff',
        enterprise_client_logo: null,
        url: '',
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        password: '',
        password_confirmation: '',
        reseller_id: currentUser.id,
      });
      setTouched({
        client_name: false,
        start_date: false,
        location: false,
        primary_name: false,
        primary_email: false,
        primary_phone: false,
        country: false,
        address: false,
        city: false,
        state: false,
        zip: false,
        enterprise_client_description: false,
        text_color: false,
        banner_color: false,
        enterprise_client_logo: false,
        url: false,
        first_name: false,
        last_name: false,
        email: false,
        username: false,
        password: false,
        password_confirmation: false,
      });
      setLogoName('No file chosen');
      setPhoneInputKey((prev) => prev + 1);
      setSelectedDate(today);
      setTimeout(() => {
        navigate(`/${username}/${URL_SEGMENTS.enterpriseClients}`);
      }, 3000);
    } catch (error) {
      setIsLoading(false);
      console.error('Error creating enterprise client:', error, 'Response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Failed to create enterprise client.';
      toast.error(errorMessage, {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
        autoClose: 3000,
      });
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      client_name: '',
      start_date: today.toISOString().split('T')[0],
      location: '',
      primary_name: '',
      primary_email: '',
      primary_phone: '',
      country: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      enterprise_client_description: '',
      text_color: '#000000',
      banner_color: '#ffffff',
      enterprise_client_logo: null,
      url: '',
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      password: '',
      password_confirmation: '',
      reseller_id: currentUser.id,
    });
    setErrors({});
    setTouched({
      client_name: false,
      start_date: false,
      location: false,
      primary_name: false,
      primary_email: false,
      primary_phone: false,
      country: false,
      address: false,
      city: false,
      state: false,
      zip: false,
      enterprise_client_description: false,
      text_color: false,
      banner_color: false,
      enterprise_client_logo: false,
      url: false,
      first_name: false,
      last_name: false,
      email: false,
      username: false,
      password: false,
      password_confirmation: false,
    });
    setPhoneInputKey((prev) => prev + 1);
    setSelectedDate(today);
    navigate(`/${username}/${URL_SEGMENTS.enterpriseClients}`);
  };

  const handleNext = async () => {
    if (activeTab === 'basic' && !(await validateTab('basic'))) {
      toast.error('Please fill all required fields correctly.', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
        autoClose: 3000,
      });
      return;
    }
    if (activeTab === 'basic') {
      setActiveTab('admin');
      setErrors({});
    }
  };

  const handlePrevious = () => {
    if (activeTab === 'admin') {
      setActiveTab('basic');
    }
    setErrors({});
  };

  return (
    <div>
      <style>
        {`
          .color-picker-container {
            position: relative;
            display: inline-block;
          }
          .color-picker {
            position: absolute;
            top: 0;
            left: 100%;
            margin-left: 10px;
            z-index: 10;
          }
          .date-input-container {
            position: relative;
            border: 1px solid ${errors.start_date && touched.start_date ? theme.colors.danger : theme.colors.borderColor};
            border-radius: 0.25rem;
            padding: 0.375rem 0.75rem;
            background-color: #fff;
            height: 35px;
          }
          .date-input-container input {
            border: none;
            width: 100%;
            padding: 0;
            background: transparent;
            font-size: 12px;
            color: #666;
            height: 100%;
            line-height: 1.5;
          }
          .date-input-container i {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            color: ${theme.colors.textPrimary};
            cursor: pointer;
          }
          .date-picker-popup {
            position: absolute;
            top: 100%;
            left: 0;
            z-index: 10;
          }
          select:focus, select:active {
            outline: none !important;
            box-shadow: none !important;
            border: 1px solid #000000 !important;
          }
        `}
      </style>

      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <div className="breadcrumb">
          <span style={{ color: theme.colors.primary }}>
          <Link to={`/${username}/${URL_SEGMENTS.enterpriseClients}`}
              style={{
                color: theme.colors.primary,
                textDecoration: 'underline',
                textDecorationColor: theme.colors.primary,
              }}
            >
              {/*  breadcrumb label from config */}
              {cfg.label} Management
            </Link>
          </span>
          <span className="breadcrumb-divider">&gt;</span>
          {/*  page title from config */}
          <span style={{ color: theme.colors.textPrimary }}>Add New {cfg.label}</span>
        </div>
      </div>

      {/* Main Container */}
      <div
        className="add-enterprise-client-container"
        style={{
          backgroundColor: '#ffffff',
          padding: '1rem',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          marginTop: '10px',
        }}
      >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        />
        {isLoading && (
          <div className="loading-overlay">
            <div
              className="spinner"
              style={{
                border: `4px solid ${theme.colors.primary}`,
                borderTop: '4px solid transparent',
              }}
            />
          </div>
        )}

        {/*  page heading from config */}
        <h4 className="page-title" style={{ color: theme.colors.textPrimary }}>
          Add New {cfg.label}
        </h4>
        
        <div
          className="tab-content-container"
          style={{
            border: `1px solid ${theme.colors.borderColor}`,
            padding: '20px',
            borderRadius: '6px',
            backgroundColor: '#fff',
          }}
        >
          <div className="tab-controls">
            {['basic', 'admin'].map((tab, index) => (
              <a
                key={tab}
                href="#"
                className="tab-link"
                style={{ color: activeTab === tab ? theme.colors.primary : theme.colors.secondary }}
                onClick={async (e) => {
                  e.preventDefault();
                  if (activeTab === 'basic' && tab === 'admin' && !(await validateTab('basic'))) {
                    toast.error('Please fill all required fields correctly.', {
                      toastStyle: {
                        backgroundColor: theme.colors.bgPrimary,
                        border: `1px solid ${theme.colors.borderColor}`,
                        color: theme.colors.danger,
                      },
                      autoClose: 3000,
                    });
                    return;
                  }
                  setActiveTab(tab);
                  setErrors({});
                }}
              >
                <span
                  className="tab-indicator"
                  style={{
                    border: `2px solid ${activeTab === tab ? theme.colors.primary : theme.colors.secondary}`,
                    color: activeTab === tab ? theme.colors.textWhite : theme.colors.secondary,
                    backgroundColor: activeTab === tab ? theme.colors.primary : 'transparent',
                  }}
                >
                  {index + 1}
                </span>
                <span
                  className="tab-label"
                  style={{
                    borderBottom: activeTab === tab ? `2px solid ${theme.colors.primary}` : 'none',
                  }}
                >
                  {tab === 'basic' ? 'Basic Information' : 'Admin Setup'}
                </span>
              </a>
            ))}
          </div>

          {activeTab === 'basic' && (
            <div className="tab-content">
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    {/*  field label from config */}
                    {cfg.label} Name*
                  </label>
                  <input
                    type="text"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.client_name && touched.client_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.client_name && touched.client_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.client_name && touched.client_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.client_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Start Date*
                  </label>
                  <div
                    className="date-input-container"
                    ref={datePickerRef}
                  >
                    <input
                      type="text"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`form-control ${errors.start_date && touched.start_date ? 'is-invalid' : ''}`}
                      style={{
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        fontSize: '12px',
                        fontFamily: 'inherit',
                        fontWeight: '100',
                      }}
                      required
                    />
                    <span
                      ref={iconRef}
                      onMouseEnter={handleIconMouseEnter}
                      onMouseLeave={handleIconMouseLeave}
                      onClick={handleDateIconClick}
                    >
                      <i className="bi bi-calendar" />
                    </span>
                    {showDatePicker && (
                      <div
                        className="date-picker-popup"
                        ref={calendarRef}
                        onMouseEnter={handleCalendarMouseEnter}
                        onMouseLeave={handleCalendarMouseLeave}
                      >
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          dateFormat="yyyy-MM-dd"
                          inline
                          className="custom-date-picker"
                        />
                      </div>
                    )}
                  </div>
                  {errors.start_date && touched.start_date && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.start_date}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="form-control"
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Primary Contact (Name)*
                  </label>
                  <input
                    type="text"
                    name="primary_name"
                    value={formData.primary_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.primary_name && touched.primary_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.primary_name && touched.primary_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.primary_name && touched.primary_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.primary_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Primary Contact (Email)*
                  </label>
                  <input
                    type="email"
                    name="primary_email"
                    value={formData.primary_email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.primary_email && touched.primary_email ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.primary_email && touched.primary_email ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.primary_email && touched.primary_email && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.primary_email}
                    </span>
                  )}
                  {checking.email && (
                    <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.3rem', fontSize: '0.875em' }}>
                      Checking availability...
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Primary Contact (Mobile)*
                  </label>
                  <PhoneInput
                    key={phoneInputKey}
                    country={'us'}
                    value={formData.primary_phone}
                    onChange={(value) => {
                      const phoneWithPrefix = value.startsWith('+') ? value : `+${value}`;
                      setFormData((prev) => ({ ...prev, primary_phone: phoneWithPrefix }));
                      setTouched((prev) => ({ ...prev, primary_phone: true }));
                    }}
                    onBlur={async () => {
                      const phoneValue = formData.primary_phone;
                      setTouched((prev) => ({ ...prev, primary_phone: true }));
                      if (!phoneValue) {
                        setErrors((prev) => ({ ...prev, primary_phone: 'Mobile is required.' }));
                        return;
                      }
                      try {
                        const phoneToValidate = phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`;
                        const phoneNumber = parsePhoneNumber(phoneToValidate);
                        if (!phoneNumber || !phoneNumber.isValid()) {
                          setErrors((prev) => ({ ...prev, primary_phone: 'Invalid mobile number.' }));
                          return;
                        }
                        setErrors((prev) => ({ ...prev, primary_phone: '' }));
                        setChecking((prev) => ({ ...prev, mobile: true }));
                        await api.post('/check-phone', { phone: phoneToValidate });
                        setChecking((prev) => ({ ...prev, mobile: false }));
                      } catch (error) {
                        setErrors((prev) => ({ ...prev, primary_phone: 'Invalid mobile number.' }));
                        setChecking((prev) => ({ ...prev, mobile: false }));
                      }
                    }}
                    inputClass={`form-control ${errors.primary_phone && touched.primary_phone ? 'is-invalid' : ''}`}
                    inputStyle={{
                      width: '100%',
                      height: '35px',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                      backgroundColor: theme.colors.bgPrimary,
                      border: `1px solid ${errors.primary_phone && touched.primary_phone ? theme.colors.danger : theme.colors.borderColor}`,
                      borderRadius: '4px',
                      color: '#666',
                      padding: '6px 10px 6px 45px',
                    }}
                    containerStyle={{ width: '100%' }}
                    inputProps={{
                      name: 'primary_phone',
                      required: true,
                    }}
                    buttonStyle={{
                      border: `1px solid ${theme.colors.borderColor}`,
                      backgroundColor: theme.colors.bgPrimary,
                      borderRight: 'none',
                      borderRadius: '4px 0 0 4px',
                      height: '35px',
                    }}
                  />
                  {errors.primary_phone && touched.primary_phone && (
                    <div
                      className="invalid-feedback d-block"
                      style={{
                        color: theme.colors.danger,
                        display: 'block',
                        width: '100%',
                        marginTop: '0.25rem',
                        fontSize: '0.875em',
                      }}
                    >
                      {errors.primary_phone}
                    </div>
                  )}
                  {checking.mobile && (
                    <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.25rem', fontSize: '0.875em' }}>
                      Checking availability...
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Country*
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.country && touched.country ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.country && touched.country ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  {errors.country && touched.country && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.country}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Address*
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.address && touched.address ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.address && touched.address ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.address && touched.address && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.address}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    City*
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.city && touched.city ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.city && touched.city ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.city && touched.city && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.city}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    State*
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={!formData.country}
                    className={`form-control ${errors.state && touched.state ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.state && touched.state ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  >
                    <option value="">Select State</option>
                    {statesList.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && touched.state && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.state}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Zip*
                  </label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.zip && touched.zip ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.zip && touched.zip ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.zip && touched.zip && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.zip}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    {/*  description label from config */}
                    {cfg.label} Description
                  </label>
                  <textarea
                    name="enterprise_client_description"
                    value={formData.enterprise_client_description}
                    onChange={handleInputChange}
                    rows="2"
                    className="form-control"
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Website Look & Feel
                  </label>
                  <div
                    style={{
                      backgroundColor: formData.banner_color || '#ffffff',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: `1px solid ${theme.colors.borderColor}`,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    {formData.enterprise_client_logo && (
                      <img
                        src={URL.createObjectURL(formData.enterprise_client_logo)}
                        alt="Logo Preview"
                        style={{ maxWidth: '80px' }}
                      />
                    )}
                    <div>
                      <p
                        style={{
                          color: formData.text_color || '#000000',
                          marginBottom: '1rem',
                          fontSize: '12px',
                        }}
                      >
                        Sample Text
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <div className="color-picker-container">
                          <button
                            style={{
                              padding: '0.4rem',
                              fontSize: '12px',
                              borderRadius: '4px',
                              border: 'none',
                              background: showTextColorPicker
                                ? theme.gradients.primary
                                : theme.gradients.secondary,
                              color: theme.colors.textWhite,
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease, opacity 0.3s ease',
                              minWidth: '120px',
                            }}
                            onClick={toggleTextColorPicker}
                            onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                            onMouseOut={(e) => (e.target.style.opacity = '1')}
                          >
                            Change Text Color
                          </button>
                          {showTextColorPicker && (
                            <div
                              ref={textColorPickerRef}
                              className="color-picker"
                            >
                              <ChromePicker
                                color={formData.text_color || '#000000'}
                                onChange={(color) => setFormData({ ...formData, text_color: color.hex })}
                                styles={{ default: { picker: { width: '180px' } } }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="color-picker-container">
                          <button
                            style={{
                              padding: '0.4rem',
                              fontSize: '12px',
                              borderRadius: '4px',
                              border: 'none',
                              background: showBannerColorPicker
                                ? theme.gradients.primary
                                : theme.gradients.secondary,
                              color: theme.colors.textWhite,
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease, opacity 0.3s ease',
                              minWidth: '120px',
                            }}
                            onClick={toggleBannerColorPicker}
                            onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                            onMouseOut={(e) => (e.target.style.opacity = '1')}
                          >
                            Change Banner Color
                          </button>
                          {showBannerColorPicker && (
                            <div
                              ref={bannerColorPickerRef}
                              className="color-picker"
                            >
                              <ChromePicker
                                color={formData.banner_color || '#ffffff'}
                                onChange={(color) => setFormData({ ...formData, banner_color: color.hex })}
                                styles={{ default: { picker: { width: '180px' } } }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    {/*  logo label from config */}
                    {cfg.label} Logo
                  </label>
                  <div
                    className="file-input-container"
                    style={{
                      border: `1px solid ${errors.enterprise_client_logo ? theme.colors.danger : theme.colors.borderColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.25rem',
                      backgroundColor: '#fff',
                    }}
                  >
                    <label
                      htmlFor="logo-upload"
                      style={{
                        background: theme.gradients.primary,
                        color: theme.colors.textWhite,
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        borderRadius: '0.25rem',
                        marginLeft: '-8px',
                        marginRight: '10px',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                      onMouseOut={(e) => (e.target.style.opacity = '1')}
                    >
                      Browse
                    </label>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoName}</span>
                  </div>
                  <input
                    type="file"
                    id="logo-upload"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  {errors.enterprise_client_logo && (
                    <span className="error-text" style={{ color: theme.colors.danger }}>
                      {errors.enterprise_client_logo}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    URL
                  </label>
                  <input
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    className="form-control"
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                  />
                </div>
              </div>
              <div className="form-actions" style={{ marginTop: '30px' }}>
                <button
                  className="action-button"
                  style={{ background: theme.gradients.secondary, color: theme.colors.textWhite }}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  style={{ background: theme.gradients.primary, color: theme.colors.textWhite }}
                  onClick={handleNext}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {activeTab === 'admin' && (
            <div className="tab-content">
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.first_name && touched.first_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.first_name && touched.first_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.first_name && touched.first_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.first_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Last Name*
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.last_name && touched.last_name ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.last_name && touched.last_name ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.last_name && touched.last_name && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.last_name}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Email*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.email && touched.email ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.email && touched.email && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.email}
                    </span>
                  )}
                  {checking.email && (
                    <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.3rem', fontSize: '0.875em' }}>
                      Checking availability...
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Username*
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.username && touched.username ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.username && touched.username ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.username && touched.username && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.username}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Password*
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.password && touched.password ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.password && touched.password && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.password}
                    </span>
                  )}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>
                    Confirm Password*
                  </label>
                  <input
                    type="password"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.password_confirmation && touched.password_confirmation ? 'is-invalid' : ''}`}
                    style={{
                      backgroundColor: theme.colors.bgPrimary,
                      color: '#666',
                      border: `1px solid ${errors.password_confirmation && touched.password_confirmation ? theme.colors.danger : theme.colors.borderColor}`,
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      fontWeight: '100',
                    }}
                    required
                  />
                  {errors.password_confirmation && touched.password_confirmation && (
                    <span className="invalid-feedback" style={{ color: theme.colors.danger }}>
                      {errors.password_confirmation}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="action-button"
                  style={{ background: theme.gradients.secondary, color: theme.colors.textWhite }}
                  onClick={handlePrevious}
                >
                  Previous
                </button>
                <button
                  className="action-button"
                  style={{ background: theme.gradients.primary, color: theme.colors.textWhite, opacity: isLoading ? 0.6 : 1 }}
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddEnterpriseClient;